<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="1200px"
    scrollable
    persistent
  >
    <v-card class="rounded-xl border-light bg-white">
      <!-- Toolbar del Modal -->
      <v-toolbar flat color="white" class="border-bottom" height="60">
        <v-icon color="primary" class="mr-2">mdi-account-group-outline</v-icon>
        <v-toolbar-title class="font-weight-bold grey--text text--darken-4 text-subtitle-1">
          Resumen por Cliente: {{ ecpName }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Contenido / Tabla de Resumen -->
      <v-card-text class="pa-4">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
          <div class="caption grey--text mt-2">Cargando consolidado por cliente...</div>
        </div>

        <div v-else class="table-responsive">
          <v-data-table
            :headers="summaryHeaders"
            :items="summaryList"
            dense
            hide-default-footer
            :items-per-page="-1"
            class="border-rounded border-light elevation-0"
          >
            <!-- Cliente -->
            <template v-slot:item.CustomerName="{ item }">
              <span class="font-weight-bold grey--text text--darken-4">
                {{ item.CustomerName || 'Sin Cliente' }}
              </span>
            </template>

            <!-- Ppto TM -->
            <template v-slot:item.BudgetTons="{ item }">
              <span>{{ formatNumber(item.BudgetTons, 2) }}</span>
            </template>

            <!-- Prog TM -->
            <template v-slot:item.ProgrammedTons="{ item }">
              <span class="font-weight-bold primary--text">{{ formatNumber(item.ProgrammedTons, 2) }}</span>
            </template>

            <!-- Saldo TM -->
            <template v-slot:item.BalanceTons="{ item }">
              <span class="font-weight-bold" :class="item.BalanceTons < 0 ? 'red--text' : 'green--text text--darken-3'">
                {{ formatNumber(item.BalanceTons, 2) }}
                <span v-if="item.BalanceTons < 0" class="caption">⚠️</span>
              </span>
            </template>

            <!-- Ppto FCL -->
            <template v-slot:item.BudgetFcl="{ item }">
              <span>{{ formatNumber(item.BudgetFcl, 2) }}</span>
            </template>

            <!-- Prog FCL -->
            <template v-slot:item.ProgrammedFcl="{ item }">
              <span class="font-weight-medium">{{ formatNumber(item.ProgrammedFcl, 2) }}</span>
            </template>

            <!-- Saldo FCL -->
            <template v-slot:item.BalanceFcl="{ item }">
              <span class="font-weight-medium" :class="item.BalanceFcl < 0 ? 'red--text' : ''">
                {{ formatNumber(item.BalanceFcl, 2) }}
              </span>
            </template>

            <!-- Ppto FOB USD -->
            <template v-slot:item.BudgetFobUsd="{ item }">
              <span>${{ formatNumber(item.BudgetFobUsd, 2) }}</span>
            </template>

            <!-- Prog FOB USD -->
            <template v-slot:item.ProgrammedFobUsd="{ item }">
              <span class="font-weight-bold green--text text--darken-4">${{ formatNumber(item.ProgrammedFobUsd, 2) }}</span>
            </template>

            <!-- Saldo FOB USD -->
            <template v-slot:item.BalanceFobUsd="{ item }">
              <span class="font-weight-bold" :class="item.BalanceFobUsd < 0 ? 'red--text' : 'green--text text--darken-3'">
                ${{ formatNumber(item.BalanceFobUsd, 2) }}
              </span>
            </template>

            <!-- Imp Contractual -->
            <template v-slot:item.AmountContract="{ item }">
              <span class="font-weight-medium">${{ formatNumber(item.AmountContract, 2) }}</span>
            </template>

            <!-- Líneas -->
            <template v-slot:item.LineCount="{ item }">
              <v-chip x-small class="font-weight-bold" color="grey lighten-4">{{ item.LineCount }}</v-chip>
            </template>

            <!-- Slot Footer / Totales -->
            <template v-slot:body.append>
              <tr class="font-weight-bold grey lighten-4">
                <td class="font-weight-bold">TOTALES</td>
                <td class="text-right">{{ formatNumber(totals.BudgetTons, 2) }}</td>
                <td class="text-right primary--text">{{ formatNumber(totals.ProgrammedTons, 2) }}</td>
                <td class="text-right" :class="totals.BalanceTons < 0 ? 'red--text' : 'green--text text--darken-3'">
                  {{ formatNumber(totals.BalanceTons, 2) }}
                </td>
                <td class="text-right">{{ formatNumber(totals.BudgetFcl, 2) }}</td>
                <td class="text-right">{{ formatNumber(totals.ProgrammedFcl, 2) }}</td>
                <td class="text-right" :class="totals.BalanceFcl < 0 ? 'red--text' : ''">
                  {{ formatNumber(totals.BalanceFcl, 2) }}
                </td>
                <td class="text-right">${{ formatNumber(totals.BudgetFobUsd, 2) }}</td>
                <td class="text-right green--text text--darken-4">${{ formatNumber(totals.ProgrammedFobUsd, 2) }}</td>
                <td class="text-right" :class="totals.BalanceFobUsd < 0 ? 'red--text' : 'green--text text--darken-3'">
                  ${{ formatNumber(totals.BalanceFobUsd, 2) }}
                </td>
                <td class="text-right">${{ formatNumber(totals.AmountContract, 2) }}</td>
                <td class="text-center">{{ totals.LineCount }}</td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4 border-top">
        <v-spacer />
        <v-btn text small class="text-capitalize" @click="closeModal">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import CommercialProgrammingService from "@/services/ExternalTrade/CommercialProgrammingService";

export default {
  name: "CommercialProgrammingCustomerSummaryModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    ecpId: {
      type: Number,
      required: true,
    },
    ecpName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      summaryList: [],
    };
  },
  computed: {
    summaryHeaders() {
      return CommercialProgrammingService.getSummaryHeaders();
    },
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    totals() {
      let bTons = 0, pTons = 0, bFcl = 0, pFcl = 0, bFob = 0, pFob = 0, contract = 0, lines = 0;
      this.summaryList.forEach((s) => {
        bTons += parseFloat(s.BudgetTons) || 0;
        pTons += parseFloat(s.ProgrammedTons) || 0;
        bFcl += parseFloat(s.BudgetFcl) || 0;
        pFcl += parseFloat(s.ProgrammedFcl) || 0;
        bFob += parseFloat(s.BudgetFobUsd) || 0;
        pFob += parseFloat(s.ProgrammedFobUsd) || 0;
        contract += parseFloat(s.AmountContract) || 0;
        lines += parseInt(s.LineCount) || 0;
      });
      return {
        BudgetTons: bTons,
        ProgrammedTons: pTons,
        BalanceTons: bTons - pTons,
        BudgetFcl: bFcl,
        ProgrammedFcl: pFcl,
        BalanceFcl: bFcl - pFcl,
        BudgetFobUsd: bFob,
        ProgrammedFobUsd: pFob,
        BalanceFobUsd: bFob - pFob,
        AmountContract: contract,
        LineCount: lines,
      };
    },
  },
  async mounted() {
    await this.loadSummary();
  },
  methods: {
    async loadSummary() {
      if (!this.ecpId) return;
      try {
        this.loading = true;
        const res = await CommercialProgrammingService.summaryByCustomer(this.ecpId, this.$fun.getUserID());
        if (res && res.data) {
          this.summaryList = res.data;
        }
      } catch (error) {
        const msg = (error.response && error.response.data) || error.message;
        this.$fun.alert(msg, "error");
      } finally {
        this.loading = false;
      }
    },

    closeModal() {
      this.dialogVisible = false;
    },

    formatNumber(val, decimals = 2) {
      const num = parseFloat(val);
      if (isNaN(num)) return "0.00";
      return num.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e2e8f0;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

.border-light {
  border: 1px solid #e2e8f0;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.modern-table th {
  background-color: #f1f5f9;
  color: #334155;
  font-weight: 700;
  font-size: 0.76rem;
  text-transform: uppercase;
  padding: 10px 8px;
  border-bottom: 2px solid #cbd5e1;
  white-space: nowrap;
}

.modern-table td {
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.totals-row td {
  background-color: #f8fafc;
  border-top: 2px solid #cbd5e1;
  font-size: 0.85rem;
}
</style>
